import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: {
    title: string;
    logoSrc: string;
    logoAlt: string;
    modalImageSrc: string;
    modalImageAlt: string;
    modalImageFit?: 'contain' | 'cover';
    siteHref?: string;
    logoClassName?: string;
    logoContainerClassName?: string;
    features: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  } | null;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ 
  isOpen, 
  onClose,
  project,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const dragStateRef = useRef<{ active: boolean; lastX: number; lastY: number }>({
    active: false,
    lastX: 0,
    lastY: 0,
  });
  const pinchStateRef = useRef<{ distance: number; zoom: number } | null>(null);

  const clampedZoom = Math.min(Math.max(zoom, 1), 4);

  if (!project) {
    return null;
  }

  const imageFitClassName = project.modalImageFit === 'cover' ? 'object-cover' : 'object-contain px-3 pb-2 pt-4 sm:p-10';

  const resetPreview = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    dragStateRef.current = { active: false, lastX: 0, lastY: 0 };
    pinchStateRef.current = null;
    offsetRef.current = { x: 0, y: 0 };
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const closePreview = () => {
    resetPreview();
    setIsPreviewOpen(false);
  };

  const openPreview = () => {
    resetPreview();
    setIsPreviewOpen(true);
  };

  const updateZoom = (nextZoom: number) => {
    const boundedZoom = Math.min(Math.max(nextZoom, 1), 4);
    setZoom(boundedZoom);
    if (boundedZoom === 1) {
      offsetRef.current = { x: 0, y: 0 };
      setOffset({ x: 0, y: 0 });
    }
  };

  const updateOffset = (nextOffset: { x: number; y: number }) => {
    offsetRef.current = nextOffset;

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      setOffset(offsetRef.current);
      animationFrameRef.current = null;
    });
  };

  const getTouchDistance = (touches: React.TouchList) => {
    const [firstTouch, secondTouch] = [touches[0], touches[1]];
    return Math.hypot(secondTouch.clientX - firstTouch.clientX, secondTouch.clientY - firstTouch.clientY);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-background/80 z-40"
          />

          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-50 p-3 sm:p-4 lg:p-8 will-change-transform"
            role="dialog"
            aria-modal="true"
            aria-label={`${project.title} project details`}
          >
            <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-background/95 shadow-2xl">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div
                    className={cn(
                      'flex h-14 w-14 items-center justify-center overflow-hidden sm:h-16 sm:w-16',
                      project.logoContainerClassName,
                    )}
                  >
                    <img
                      src={project.logoSrc}
                      alt={project.logoAlt}
                      className={cn('h-full w-full object-contain', project.logoClassName)}
                    />
                  </div>
                  <div className="hidden min-w-0 sm:block">
                    <h2 className="truncate text-xl font-medium text-foreground geist-font sm:text-2xl">
                      {project.title}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-base text-muted-foreground inter-font sm:text-lg">
                  {project.siteHref && (
                    <a
                      href={project.siteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-foreground transition-colors hover:text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>Site</span>
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  )}
                  {project.siteHref && <span aria-hidden="true" className="text-foreground/50">|</span>}
                  <button
                    onClick={onClose}
                    className="inline-flex items-center rounded-lg px-3 py-2 text-foreground transition-colors hover:text-white"
                    aria-label="Close modal"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-1 flex-col overflow-hidden lg:grid lg:grid-cols-2 lg:grid-rows-1">
                <div className="relative shrink-0 border-b border-white/10 bg-gradient-to-br from-primary/10 via-background to-secondary/20 lg:min-h-0 lg:border-b-0 lg:border-r">
                  <button
                    type="button"
                    onClick={openPreview}
                    className="group flex w-full cursor-zoom-in flex-col items-center justify-start text-left lg:h-full lg:justify-center"
                    aria-label={`Open ${project.title} image preview`}
                  >
                    <img
                      src={project.modalImageSrc}
                      alt={project.modalImageAlt}
                      className={`w-full ${imageFitClassName} lg:h-full`}
                    />
                    <span className="pointer-events-none mb-4 mr-4 mt-2 inline-flex items-center gap-2 self-end rounded-full border border-white/10 bg-background/80 px-3 py-1.5 text-xs text-foreground/90 opacity-90 shadow-lg transition-opacity group-hover:opacity-100 lg:absolute lg:bottom-4 lg:right-4 lg:mb-0 lg:mr-0 lg:mt-0">
                      <ZoomIn className="h-3.5 w-3.5" />
                      Zoom
                    </span>
                  </button>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-3 p-4 sm:gap-4 sm:p-6 lg:content-center">
                  {project.features.map((feature) => (
                    <article
                      key={feature.id}
                      className="glass-card flex h-full min-h-0 flex-col justify-start overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-3 text-left sm:h-auto sm:min-h-40 sm:p-4"
                    >
                      <p className="mb-2 text-base font-medium text-foreground geist-font sm:text-lg">
                        {feature.title}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground inter-font">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {isPreviewOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={closePreview}
                  className="fixed inset-0 z-[60] bg-background/95"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="fixed inset-0 z-[70] flex flex-col"
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${project.title} image preview`}
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6">
                    <div>
                      <p className="text-sm text-foreground geist-font">{project.title}</p>
                      <p className="text-xs text-muted-foreground inter-font">Drag to move. Pinch or use controls to zoom.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateZoom(clampedZoom - 0.5)}
                        className="glass-button inline-flex h-10 w-10 items-center justify-center rounded-full"
                        aria-label="Zoom out image"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => updateZoom(clampedZoom + 0.5)}
                        className="glass-button inline-flex h-10 w-10 items-center justify-center rounded-full"
                        aria-label="Zoom in image"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={closePreview}
                        className="glass-button inline-flex h-10 w-10 items-center justify-center rounded-full"
                        aria-label="Close image preview"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div
                    className="flex-1 overflow-hidden touch-none"
                    onPointerDown={(event) => {
                      if (clampedZoom <= 1) return;
                      dragStateRef.current = {
                        active: true,
                        lastX: event.clientX,
                        lastY: event.clientY,
                      };
                    }}
                    onPointerMove={(event) => {
                      if (!dragStateRef.current.active || clampedZoom <= 1) return;
                      const deltaX = event.clientX - dragStateRef.current.lastX;
                      const deltaY = event.clientY - dragStateRef.current.lastY;
                      dragStateRef.current = {
                        active: true,
                        lastX: event.clientX,
                        lastY: event.clientY,
                      };
                      updateOffset({
                        x: offsetRef.current.x + deltaX,
                        y: offsetRef.current.y + deltaY,
                      });
                    }}
                    onPointerUp={() => {
                      dragStateRef.current.active = false;
                    }}
                    onPointerCancel={() => {
                      dragStateRef.current.active = false;
                    }}
                    onPointerLeave={() => {
                      dragStateRef.current.active = false;
                    }}
                    onTouchStart={(event) => {
                      if (event.touches.length === 2) {
                        pinchStateRef.current = {
                          distance: getTouchDistance(event.touches),
                          zoom: clampedZoom,
                        };
                      }
                    }}
                    onTouchMove={(event) => {
                      if (event.touches.length === 2 && pinchStateRef.current) {
                        event.preventDefault();
                        const nextDistance = getTouchDistance(event.touches);
                        const scaleRatio = nextDistance / pinchStateRef.current.distance;
                        updateZoom(pinchStateRef.current.zoom * scaleRatio);
                      }
                    }}
                    onTouchEnd={() => {
                      if (pinchStateRef.current && pinchStateRef.current.zoom !== clampedZoom) {
                        pinchStateRef.current = {
                          distance: pinchStateRef.current.distance,
                          zoom: clampedZoom,
                        };
                      }
                      if (pinchStateRef.current && clampedZoom <= 1) {
                        pinchStateRef.current = null;
                      }
                    }}
                  >
                    <div className="flex h-full items-center justify-center p-4 sm:p-8">
                      <img
                        src={project.modalImageSrc}
                        alt={project.modalImageAlt}
                        className="max-h-full max-w-full select-none object-contain"
                        draggable={false}
                        style={{
                          transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${clampedZoom})`,
                          transformOrigin: 'center center',
                          transition: 'none',
                          willChange: 'transform',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

